import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "../../lib/auth";
import Seo from "../../components/Seo";

const SEC_QS = [
  "What was the name of your first pet?",
  "What city were you born in?",
  "What was the model of your first car?",
  "What was the name of your elementary school?",
  "What is your mother's maiden name?",
  "What was the street name you grew up on?",
];

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "",
    password: "", confirm: "",
    security_q1: SEC_QS[0], security_a1: "",
    security_q2: SEC_QS[1], security_a2: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return setErr("Passwords don't match.");
    if (form.security_q1 === form.security_q2) return setErr("Please pick two different security questions.");
    if (!form.security_a1.trim() || !form.security_a2.trim()) return setErr("Answer both security questions.");
    setBusy(true);
    try {
      const u = await signup({
        name: form.name, email: form.email, phone: form.phone, address: form.address,
        password: form.password,
        security_q1: form.security_q1, security_a1: form.security_a1,
        security_q2: form.security_q2, security_a2: form.security_a2,
      });
      const { isDealerUser } = await import("../../lib/roles");
      nav(isDealerUser(u) ? "/dealer" : "/my-account", { replace: true });
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main-content" className="bg-[var(--ssc-paper)] min-h-[80vh] py-12">
      <Seo title="Create Account | Scott Sells Chevys" description="Create a free Scott Sells Chevys account to save your trade estimates and deals." />
      <div className="max-w-xl mx-auto px-4">
        <div className="ssc-card">
          <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[var(--ssc-blue)]">Create Account</div>
          <h1 className="text-3xl font-black text-[var(--ssc-navy)] mt-1">Save your deals</h1>
          <p className="text-[var(--ssc-ink-2)] mt-1 text-sm">
            Free account — save trade estimates, build-your-own deals, and favorite vehicles. Scott can pull up your saved deal anytime with the code.
          </p>

          <form onSubmit={submit} className="mt-5 space-y-3" data-testid="signup-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Full name *"><input data-testid="signup-name" className="ssc-input" value={form.name} onChange={set("name")} required /></Field>
              <Field label="Email *"><input data-testid="signup-email" type="email" className="ssc-input" value={form.email} onChange={set("email")} required autoComplete="email" /></Field>
              <Field label="Phone *"><input data-testid="signup-phone" className="ssc-input" value={form.phone} onChange={set("phone")} required autoComplete="tel" /></Field>
              <Field label="Address"><input data-testid="signup-address" className="ssc-input" value={form.address} onChange={set("address")} autoComplete="street-address" /></Field>
              <Field label="Password *"><input data-testid="signup-password" type="password" className="ssc-input" value={form.password} onChange={set("password")} required minLength={8} autoComplete="new-password" /></Field>
              <Field label="Confirm password *"><input data-testid="signup-confirm" type="password" className="ssc-input" value={form.confirm} onChange={set("confirm")} required autoComplete="new-password" /></Field>
            </div>

            <div className="pt-3 border-t border-[var(--ssc-line)] mt-4">
              <div className="text-xs font-extrabold uppercase tracking-widest text-[var(--ssc-blue)] mb-2">Security Questions (for password reset)</div>
              <Field label="Question 1">
                <select data-testid="signup-q1" className="ssc-select" value={form.security_q1} onChange={set("security_q1")}>
                  {SEC_QS.map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </Field>
              <Field label="Answer 1 *"><input data-testid="signup-a1" className="ssc-input mt-2" value={form.security_a1} onChange={set("security_a1")} required /></Field>
              <Field label="Question 2" className="mt-3">
                <select data-testid="signup-q2" className="ssc-select" value={form.security_q2} onChange={set("security_q2")}>
                  {SEC_QS.map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </Field>
              <Field label="Answer 2 *"><input data-testid="signup-a2" className="ssc-input mt-2" value={form.security_a2} onChange={set("security_a2")} required /></Field>
            </div>

            {err && <div data-testid="signup-error" className="text-sm text-[var(--ssc-red)]">{err}</div>}
            <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary w-full" data-testid="signup-submit">
              {busy ? "Creating account…" : "Create Account"}
            </button>
          </form>

          <div className="mt-4 text-sm text-[var(--ssc-ink-2)]">
            Already have an account? <Link to="/login" className="text-[var(--ssc-blue)] font-semibold" data-testid="signup-go-login">Sign in →</Link>
          </div>
        </div>
      </div>
    </main>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="ssc-label">{label}</label>
    {children}
  </div>
);
